import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { sortBy, uniqBy } from "lodash";
import { getRetailNetworksRoute } from "../../lib/routes";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import Message from "../../components/Message/Message";
import {
  getObjectTypesRoute,
  fetchDictionariesRoute,
  fetchTerminVersionsRoute,
  getDictByTypeRoute,
} from "../../lib/routes/dictionaries";
import { getSequenceRoute, updateSequenceRoute } from "../../lib/routes/sequences";
import { DictionaryDto, ObjectType, Property, Sequence } from "../../lib/interfaces";
import { DictionaryTypeAlias, LoadingStatusCode } from "../../lib/types";
import { catchApiError } from "../../lib/utils/catchApiError";
import { RateTypes } from "../../lib/const/rateTypes";
import { RateModes } from "../../lib/const/rateModes";
import { PriceUnits } from "../../lib/const/priceUnits";
import { TariffModelTypes } from "../../lib/const/tariffModelTypes";
import { TariffPeriodAlignments } from "../../lib/const/tariffPeriodAlignments";

interface InitialState {
  actionType: DictionaryDto[];
  actionTypeLoading: LoadingStatusCode;
  agrStatus: DictionaryDto[];
  agrStatusLoading: LoadingStatusCode;
  billStatus: DictionaryDto[];
  billStatusLoading: LoadingStatusCode;
  billType: DictionaryDto[];
  billTypeLoading: LoadingStatusCode;
  blockingObjectType: DictionaryDto[];
  blockingObjectTypeLoading: LoadingStatusCode;
  blockingReason: DictionaryDto[];
  blockingReasonLoading: LoadingStatusCode;
  blockingSource: DictionaryDto[];
  blockingSourceLoading: LoadingStatusCode;
  chargeNames: DictionaryDto[];
  chargeNamesLoading: LoadingStatusCode;
  chargeNameTypeStatus: DictionaryDto[];
  chargeNameTypeStatusLoading: LoadingStatusCode;
  conditionTypes: DictionaryDto[];
  conditionTypesLoading: LoadingStatusCode;
  dicCurrencies: DictionaryDto[];
  dicCurrenciesLoading: LoadingStatusCode;
  dicAdjustmentType: DictionaryDto[];
  dicAdjustmentTypeLoading: LoadingStatusCode;
  dicAdjustmentReason: DictionaryDto[];
  dicAdjustmentReasonLoading: LoadingStatusCode;
  dicDataTypes: DictionaryDto[];
  dicDataTypesLoading: LoadingStatusCode;
  dicStorageTypes: DictionaryDto[];
  dicStorageTypesLoading: LoadingStatusCode;
  dicViewModeMasks: DictionaryDto[];
  dicViewModeMasksLoading: LoadingStatusCode;
  dicMultiplType: DictionaryDto[];
  dicMultiplTypeLoading: LoadingStatusCode;
  dicRequiredType: DictionaryDto[];
  dicRequiredTypeLoading: LoadingStatusCode;
  dicRoutingOrdersStatuses: DictionaryDto[];
  dicRoutingOrdersStatusesLoading: LoadingStatusCode;
  dictionaries?: Record<DictionaryTypeAlias, DictionaryDto[]>;
  dictionariesLoading: LoadingStatusCode;
  dicTypeWithParameter: DictionaryDto[];
  dicTypeWithParameterLoading: LoadingStatusCode;
  dicTypeWithParameterSpecific: DictionaryDto[];
  dicTypeWithParameterSpecificLoading: LoadingStatusCode;
  dicVersions: DictionaryDto[];
  dicVersionsLoading: LoadingStatusCode;
  invoicePeriodStatus: DictionaryDto[];
  invoicePeriodStatusLoading: LoadingStatusCode;
  objectTypes: ObjectType[];
  objectTypesLoading: LoadingStatusCode;
  paymentSourceTypes: DictionaryDto[];
  paymentSourceTypesLoading: LoadingStatusCode;
  paymentStatuses: DictionaryDto[];
  paymentStatusesLoading: LoadingStatusCode;
  paymentType: DictionaryDto[];
  paymentTypeLoading: LoadingStatusCode;
  paymentTypeStatus: DictionaryDto[];
  paymentTypeStatusLoading: LoadingStatusCode;
  payStatus: DictionaryDto[];
  payStatusLoading: LoadingStatusCode;
  periodTypes: DictionaryDto[];
  periodTypesLoading: LoadingStatusCode;
  prePaymentPeriodStatus: DictionaryDto[];
  prePaymentPeriodStatusLoading: LoadingStatusCode;
  priceEvents: DictionaryDto[];
  priceEventsLoading: LoadingStatusCode;
  priceUnits: DictionaryDto<PriceUnits>[];
  priceUnitsLoading: LoadingStatusCode;
  glsTypes: DictionaryDto[];
  glsTypesLoading: LoadingStatusCode;
  glsStatuses: DictionaryDto[];
  glsStatusesLoading: LoadingStatusCode;
  reportType: DictionaryDto[];
  reportTypeLoading: LoadingStatusCode;
  products: DictionaryDto[];
  productsLoading: LoadingStatusCode;
  profileTypeStatus: DictionaryDto[];
  profileTypeStatusLoading: LoadingStatusCode;
  rateModesEvents: DictionaryDto<RateModes>[];
  rateModesEventsLoading: LoadingStatusCode;
  rateType: DictionaryDto<RateTypes>[];
  rateTypeLoading: LoadingStatusCode;
  requestStatus: DictionaryDto[];
  requestStatusLoading: LoadingStatusCode;
  retailNetworks: { retailNetworksDic: Property[]; retailNetworksUniqueIdDic: Property[] };
  retailNetworksLoading: LoadingStatusCode;
  sequence: Sequence[];
  sequenceLoading: LoadingStatusCode;
  serviceMode: DictionaryDto[];
  serviceModeLoading: LoadingStatusCode;
  serviceStatus: DictionaryDto[];
  serviceStatusLoading: LoadingStatusCode;
  serviceUsageDirection: DictionaryDto[];
  serviceUsageDirectionLoading: LoadingStatusCode;
  serviceUsageDocumentType: DictionaryDto[];
  serviceUsageDocumentTypeLoading: LoadingStatusCode;
  tariffModelTypes: DictionaryDto<TariffModelTypes>[];
  tariffModelTypesLoading: LoadingStatusCode;
  tariffPeriodAlignments: DictionaryDto<TariffPeriodAlignments>[];
  tariffPeriodAlignmentsLoading: LoadingStatusCode;
  tariffStatuses: DictionaryDto[];
  tariffStatusesLoading: LoadingStatusCode;
  tariffTypes: DictionaryDto[];
  tariffTypesLoading: LoadingStatusCode;
  taskStatus: DictionaryDto[];
  taskStatusLoading: LoadingStatusCode;
  trafficStatus: DictionaryDto[];
  trafficStatusLoading: LoadingStatusCode;
  unBlockingReason: DictionaryDto[];
  unBlockingReasonLoading: LoadingStatusCode;
  uniqUsers: DictionaryDto[];
  userScope: DictionaryDto[];
  userScopeLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  actionType: [],
  actionTypeLoading: rtkRequestStatusCodes.idle,
  agrStatus: [],
  agrStatusLoading: rtkRequestStatusCodes.idle,
  billStatus: [],
  billStatusLoading: rtkRequestStatusCodes.idle,
  billType: [],
  billTypeLoading: rtkRequestStatusCodes.idle,
  blockingObjectType: [],
  blockingObjectTypeLoading: rtkRequestStatusCodes.idle,
  blockingReason: [],
  blockingReasonLoading: rtkRequestStatusCodes.idle,
  blockingSource: [],
  blockingSourceLoading: rtkRequestStatusCodes.idle,
  chargeNames: [],
  chargeNamesLoading: rtkRequestStatusCodes.idle,
  chargeNameTypeStatus: [],
  chargeNameTypeStatusLoading: rtkRequestStatusCodes.idle,
  conditionTypes: [],
  conditionTypesLoading: rtkRequestStatusCodes.idle,
  dicCurrencies: [],
  dicCurrenciesLoading: rtkRequestStatusCodes.idle,
  dicAdjustmentType: [],
  dicAdjustmentTypeLoading: rtkRequestStatusCodes.idle,
  dicAdjustmentReason: [],
  dicAdjustmentReasonLoading: rtkRequestStatusCodes.idle,
  dicDataTypes: [],
  dicDataTypesLoading: rtkRequestStatusCodes.idle,
  dicStorageTypes: [],
  dicStorageTypesLoading: rtkRequestStatusCodes.idle,
  dicViewModeMasks: [],
  dicViewModeMasksLoading: rtkRequestStatusCodes.idle,
  dicMultiplType: [],
  dicMultiplTypeLoading: rtkRequestStatusCodes.idle,
  dicRequiredType: [],
  dicRequiredTypeLoading: rtkRequestStatusCodes.idle,
  dicRoutingOrdersStatuses: [],
  dicRoutingOrdersStatusesLoading: rtkRequestStatusCodes.idle,
  dictionariesLoading: rtkRequestStatusCodes.idle,
  dicTypeWithParameter: [],
  dicTypeWithParameterLoading: rtkRequestStatusCodes.idle,
  dicTypeWithParameterSpecific: [],
  dicTypeWithParameterSpecificLoading: rtkRequestStatusCodes.idle,
  dicVersions: [],
  dicVersionsLoading: rtkRequestStatusCodes.idle,
  invoicePeriodStatus: [],
  invoicePeriodStatusLoading: rtkRequestStatusCodes.idle,
  objectTypes: [],
  objectTypesLoading: rtkRequestStatusCodes.idle,
  paymentSourceTypes: [],
  paymentSourceTypesLoading: rtkRequestStatusCodes.idle,
  paymentStatuses: [],
  paymentStatusesLoading: rtkRequestStatusCodes.idle,
  paymentType: [],
  paymentTypeLoading: rtkRequestStatusCodes.idle,
  paymentTypeStatus: [],
  paymentTypeStatusLoading: rtkRequestStatusCodes.idle,
  payStatus: [],
  payStatusLoading: rtkRequestStatusCodes.idle,
  periodTypes: [],
  periodTypesLoading: rtkRequestStatusCodes.idle,
  prePaymentPeriodStatus: [],
  prePaymentPeriodStatusLoading: rtkRequestStatusCodes.idle,
  priceEvents: [],
  priceEventsLoading: rtkRequestStatusCodes.idle,
  priceUnits: [],
  priceUnitsLoading: rtkRequestStatusCodes.idle,
  glsTypes: [],
  glsTypesLoading: rtkRequestStatusCodes.idle,
  glsStatuses: [],
  glsStatusesLoading: rtkRequestStatusCodes.idle,
  reportType: [],
  reportTypeLoading: rtkRequestStatusCodes.idle,
  products: [],
  productsLoading: rtkRequestStatusCodes.idle,
  profileTypeStatus: [],
  profileTypeStatusLoading: rtkRequestStatusCodes.idle,
  rateModesEvents: [],
  rateModesEventsLoading: rtkRequestStatusCodes.idle,
  rateType: [],
  rateTypeLoading: rtkRequestStatusCodes.idle,
  requestStatus: [],
  requestStatusLoading: rtkRequestStatusCodes.idle,
  retailNetworks: { retailNetworksDic: [], retailNetworksUniqueIdDic: [] },
  retailNetworksLoading: rtkRequestStatusCodes.idle,
  sequence: [],
  sequenceLoading: rtkRequestStatusCodes.idle,
  serviceMode: [],
  serviceModeLoading: rtkRequestStatusCodes.idle,
  serviceStatus: [],
  serviceStatusLoading: rtkRequestStatusCodes.idle,
  serviceUsageDirection: [],
  serviceUsageDirectionLoading: rtkRequestStatusCodes.idle,
  serviceUsageDocumentType: [],
  serviceUsageDocumentTypeLoading: rtkRequestStatusCodes.idle,
  tariffModelTypes: [],
  tariffModelTypesLoading: rtkRequestStatusCodes.idle,
  tariffPeriodAlignments: [],
  tariffPeriodAlignmentsLoading: rtkRequestStatusCodes.idle,
  tariffStatuses: [],
  tariffStatusesLoading: rtkRequestStatusCodes.idle,
  tariffTypes: [],
  tariffTypesLoading: rtkRequestStatusCodes.idle,
  taskStatus: [],
  taskStatusLoading: rtkRequestStatusCodes.idle,
  trafficStatus: [],
  trafficStatusLoading: rtkRequestStatusCodes.idle,
  unBlockingReason: [],
  unBlockingReasonLoading: rtkRequestStatusCodes.idle,
  uniqUsers: [],
  userScope: [],
  userScopeLoading: rtkRequestStatusCodes.idle,
};

const fetchPaymentSourceTypes = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPaymentSourceTypes",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("payment_source_type"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей. Попробуйте позже",
      });
    }
  }
);

const fetchReportType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchReportType",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("report_type"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей. Попробуйте позже",
      });
    }
  }
);
const fetchProducts = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchProducts",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("product"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей. Попробуйте позже",
      });
    }
  }
);

const fetchAgrStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchAgrStatus",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("agr_status"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей. Попробуйте позже",
      });
    }
  }
);

const fetchBillStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchBillStatus",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("bill_status"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки словарей. Попробуйте позже",
      });
    }
  }
);

const fetchServiceUsageDirection = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchServiceUsageDirection",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(
      getDictByTypeRoute("service_usage_direction")
    );
    return data;
  }
);

const fetchServiceUsageDocumentType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchServiceUsageDocumentType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(
      getDictByTypeRoute("service_usage_document_type")
    );
    return data;
  }
);

const fetchRetailNetworks = createAsyncThunk<Property[]>(
  "rtkDictionaries/fetchfetchRetailNetworks",
  async () => {
    try {
      const { data } = await axios.get<Property[]>(getRetailNetworksRoute());
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка получения списка сетей",
      });
    }
  }
);

const fetchDicType = createAsyncThunk<DictionaryDto[], DictionaryTypeAlias>(
  "rtkDictionaries/fetchDicType",
  async (dicTypeAlias) => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute(dicTypeAlias));
    return data;
  }
);

const fetchDicTypeSpecific = createAsyncThunk<DictionaryDto[], DictionaryTypeAlias>(
  "rtkDictionaries/fetchDicTypeSpecific",
  async (dicTypeAlias) => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute(dicTypeAlias));
    return data;
  }
);

const fetchServiceStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchServiceStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("service_status"));
    return data;
  }
);
const fetchServiceMode = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchServiceMode",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("service_mode"));
    return data;
  }
);

const fetchBillType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchBillType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("bill_type"));
    return data;
  }
);

const fetchPayStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPayStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("pay_status"));
    return data;
  }
);

const fetchBlockingObjectType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchBlockingObjectType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("entity_object_type"));
    return data;
  }
);

const fetchBlockingSource = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchBlockingSource",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("blocking_source_type"));
    return data;
  }
);

const fetchRequestStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchRequestStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(
      getDictByTypeRoute("blocking_request_status")
    );
    return data;
  }
);

const fetchBlockingReason = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchBlockingReason",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("blocking_reason"));
    return data;
  }
);

const fetchUnBlockingReason = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchUnBlockingReason",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("unblocking_reason"));
    return data;
  }
);

const fetchUserScope = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchUserScope",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("user_scope"));
    return data;
  }
);

const fetchTaskStatusDict = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchTaskStatusDict",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("task_status"));
    return data;
  }
);

const fetchDicActionType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicActionType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("action_type"));
    return data;
  }
);

const fetchDicDataType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicDataType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("data_type"));
    return data;
  }
);

const fetchDicViewModeMask = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicViewModeMask",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("view_mode_mask"));
    return data;
  }
);

const fetchDicStorageType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicStorageType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("storage_type"));
    return data;
  }
);

const fetchDicRequiredType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicRequiredType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("required_type"));
    return data;
  }
);

const fetchDicMultiplType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicMultiplType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("multipl_type"));
    return data;
  }
);

const fetchDicAdjustmentType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicAdjustmentType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("adjustment_type"));
    return data;
  }
);

const fetchDicAdjustmentReason = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchDicAdjustmentReason",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("adjustment_reason"));
    return data;
  }
);

const fetchPaymentType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPaymentType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("payment_type"));
    return data;
  }
);

const fetchRateType = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchRateType",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("rate_type"));
    return data;
  }
);

const fetchRateModes = createAsyncThunk<DictionaryDto<RateModes>[]>(
  "rtkDictionaries/fetchRateModes",
  async () => {
    const { data } = await axios.get<DictionaryDto<RateModes>[]>(getDictByTypeRoute("rate_mode"));
    return data;
  }
);

const fetchConditionTypes = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchConditionTypes",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("condition_type"));
    return data;
  }
);

const fetchPriceEvents = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPriceEvents",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("price_event"));
    return data;
  }
);

const fetchObjectTypes = createAsyncThunk<ObjectType[]>(
  "rtkDictionaries/fetchObjectTypes",
  async () => {
    const { data } = await axios.get<ObjectType[]>(getObjectTypesRoute());
    return data;
  }
);

const fetchDicVersions = createAsyncThunk<
  DictionaryDto[],
  { dicTypeAlias: DictionaryTypeAlias; id: number }
>("rtkDictionaries/fetchDicVersions", async (props) => {
  try {
    const response = axios.get<DictionaryDto[]>(fetchTerminVersionsRoute(props));
    const { data } = await response;
    return data.sort((a, b) => +new Date(b.toDate) - +new Date(a.toDate));
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки словарей - /dictionaries. Попробуйте позже",
    });
  }
});

const fetchTrafficStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchTrafficStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("service_usage_status"));
    return data;
  }
);

const fetchDictionaries = createAsyncThunk<Record<DictionaryTypeAlias, DictionaryDto[]>>(
  "rtkDictionaries/fetchDictionaries",
  async () => {
    const { data } = await axios.get(fetchDictionariesRoute());
    return data;
  }
);

export const fetchTariffModelTypes = createAsyncThunk<DictionaryDto<TariffModelTypes>[]>(
  "rtkDictionaries/fetchTariffModelTypes",
  async () => {
    const url = getDictByTypeRoute("tariff_model_type");
    const { data } = await axios.get<DictionaryDto<TariffModelTypes>[]>(url);
    return data;
  }
);

export const fetchTariffPeriodAlignments = createAsyncThunk<
  DictionaryDto<TariffPeriodAlignments>[]
>("rtkDictionaries/fetchTariffPeriodAlignments", async () => {
  const url = getDictByTypeRoute("tariff_period_alignment");
  const { data } = await axios.get<DictionaryDto<TariffPeriodAlignments>[]>(url);
  return data;
});

const fetchCurrenciesDic = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchCurrenciesDic",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("currency"));
    return data;
  }
);

const fetchInvoicePeriodStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/invoicePeriodStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("invoice_period"));
    return data;
  }
);

const fetchPaymentTypeStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPaymentTypeStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("payment_type"));
    return data;
  }
);

const fetchPrePaymentPeriodStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPrePaymentPeriodStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("prepayment_period"));
    return data;
  }
);

const fetchProfileTypeStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchProfileTypeStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("profile_type"));
    return data;
  }
);

export const fetchTariffStatuses = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchTariffStatuses",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("tariff_status"));
    return data;
  }
);

export const fetchPaymentStatuses = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPaymentStatuses",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("payment_status"));
    return data;
  }
);

export const fetchTariffTypes = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchTariffTypes",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("tariff_type"));
    return data;
  }
);

export const fetchPeriodTypes = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPeriodTypes",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("period_type"));
    return data;
  }
);

export const fetchPriceUnits = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchPriceUnits",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("price_unit"));
    return data;
  }
);

export const fetchGlsTypes = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchGlsTypes",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("gls_type"));
    return data;
  }
);

export const fetchGlsStatuses = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchGlsStatuses",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("gls_status"));
    return data;
  }
);

export const fetchChargeNames = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchChargeNames",
  async () => {
    try {
      const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("charge_name"));
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка получения словарных терминов словаря charge_name",
      });
    }
  }
);
const fetchChargeNameTypeStatus = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/fetchChargeNameTypeStatus",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("charge_name"));
    return data.sort((a, b) => (a.name > b.name ? 1 : -1));
  }
);

const updateSequence = createAsyncThunk<Promise<any>, { seqName: string; nextValue: number }>(
  "rtkDictionaries/updateSequence",
  async ({ seqName, nextValue }) => {
    try {
      const response = axios.patch(updateSequenceRoute(seqName, nextValue));
      const { data } = await response;
      const { status } = await response;
      Message("Значение успешно обновлено", status);
      return data;
    } catch (e) {
      Message("Ошибка обновления", e);
    }
  }
);

const fetchSequence = createAsyncThunk<Sequence[]>("rtkDictionaries/fetchSequence", async () => {
  const { data } = await axios.get<Sequence[]>(getSequenceRoute());
  try {
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки данных. Попробуйте позже",
    });
  }
});

const fetchDicRoutingOrdersStatuses = createAsyncThunk<DictionaryDto[]>(
  "rtkDictionaries/DicRoutingOrderStatuses",
  async () => {
    const { data } = await axios.get<DictionaryDto[]>(getDictByTypeRoute("order_status"));
    try {
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки справочника статусов заказа. Попробуйте позже",
      });
    }
  }
);

const updateRetailNetworks = (retailNetworks: Property[]) => {
  // Временная мера - схлопывание по уникальному objectId, т.к. с бека приходят сети с повторяющимися objectId
  const retailNetworksUniqueIdDic: Property[] = [];
  retailNetworks.forEach((network) => {
    const addedNetwork = retailNetworksUniqueIdDic.find(
      (item) => item.objectId === network.objectId
    );
    if (!addedNetwork) retailNetworksUniqueIdDic.push(network);
  });

  // Для сетей с множественными аккаунтами (при одинаковом названии - записи с разными objectId), создается одна запись
  // objectId которой содержит массив objectId
  const accountNetworkObjectIds: { [key: string]: number[] } = {};
  retailNetworksUniqueIdDic.forEach(({ objectId, stringValue }) => {
    if (!stringValue) return;
    if (accountNetworkObjectIds[stringValue]) {
      if (!accountNetworkObjectIds[stringValue].includes(objectId)) {
        accountNetworkObjectIds[stringValue].push(objectId);
      }
    } else {
      accountNetworkObjectIds[stringValue] = [objectId];
    }
  });

  // Схлопывание по имени
  const retailNetworksDic: Property[] = [];
  retailNetworksUniqueIdDic.forEach((network) => {
    const networkName = network.stringValue;
    const addedNetwork = retailNetworksDic.find((item) => item.stringValue === networkName);
    if (!addedNetwork && networkName) {
      if (accountNetworkObjectIds[networkName].length > 1) {
        retailNetworksDic.push({
          ...network,
          // Для сетей с множественными аккаунтами, в objectId кладется строка из всех значений objectId для сети
          // @ts-ignore TODO - разобраться, исправить
          objectId: accountNetworkObjectIds[networkName].join("&&&"),
        });
      } else {
        retailNetworksDic.push(network);
      }
    }
  });

  return { retailNetworksDic, retailNetworksUniqueIdDic };
};

const slice = createSlice({
  name: "rtkDictionaries",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDictionaries.fulfilled, (state, { payload }) => {
        state.dictionaries = payload;
        state.dictionariesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDictionaries.pending, (state) => {
        state.dictionariesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDictionaries.rejected, (state) => {
        state.dictionariesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchTariffPeriodAlignments.fulfilled, (state, { payload }) => {
        state.tariffPeriodAlignments = payload;
        state.tariffPeriodAlignmentsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTariffPeriodAlignments.pending, (state) => {
        state.tariffPeriodAlignmentsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTariffModelTypes.fulfilled, (state, { payload }) => {
        state.tariffModelTypes = payload;
        state.tariffModelTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTariffModelTypes.pending, (state) => {
        state.tariffModelTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchServiceUsageDirection.fulfilled, (state, { payload }) => {
        // Если значения "Все" нет в словаре, добавляем на фронте
        const allDerectionsItem = payload.find(({ name }) => name === "Все");
        const updatedDictionary = [...payload];
        if (!allDerectionsItem) {
          updatedDictionary.push({
            id: 3,
            fromDate: "1970-01-01T03:00:00+03:00",
            toDate: "3000-01-01T02:59:59+03:00",
            dicTypeId: 143,
            dicTypeAlias: "service_usage_direction",
            name: "Все",
            abbrev: null,
            parentDicId: null,
            dsc: null,
            alias: "all",
          });
        }
        state.serviceUsageDirection = updatedDictionary;
        state.serviceUsageDirectionLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceUsageDirection.pending, (state) => {
        state.serviceUsageDirectionLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchServiceUsageDocumentType.fulfilled, (state, { payload }) => {
        state.serviceUsageDocumentType = payload;
        state.serviceUsageDocumentTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceUsageDocumentType.pending, (state) => {
        state.serviceUsageDocumentTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchRetailNetworks.fulfilled, (state, { payload }) => {
        if (payload) {
          state.retailNetworks = updateRetailNetworks(payload);
        } else {
          state.retailNetworks = { retailNetworksDic: [], retailNetworksUniqueIdDic: [] };
        }
        state.retailNetworksLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchRetailNetworks.pending, (state) => {
        state.retailNetworksLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchRetailNetworks.rejected, (state) => {
        state.retailNetworksLoading = rtkRequestStatusCodes.rejected;
        state.retailNetworks = { retailNetworksDic: [], retailNetworksUniqueIdDic: [] };
      })
      .addCase(fetchBillStatus.fulfilled, (state, { payload }) => {
        state.billStatus = payload;
        state.billStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchBillType.fulfilled, (state, { payload }) => {
        state.billType = payload;
        state.billTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchAgrStatus.pending, (state) => {
        state.agrStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchAgrStatus.fulfilled, (state, { payload }) => {
        state.agrStatus = [...payload].sort((a, b) => (a.id > b.id ? 1 : -1));
        state.agrStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchAgrStatus.rejected, (state, action) => {
        state.agrStatusLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchPaymentSourceTypes.pending, (state) => {
        state.paymentSourceTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPaymentSourceTypes.fulfilled, (state, { payload }) => {
        state.paymentSourceTypes = payload;
        state.paymentSourceTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPaymentSourceTypes.rejected, (state) => {
        state.paymentSourceTypesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchReportType.fulfilled, (state, { payload }) => {
        state.reportType = payload;
        state.reportTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchReportType.pending, (state) => {
        state.reportTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchReportType.rejected, (state) => {
        state.reportTypeLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.productsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchProducts.fulfilled, (state, { payload }) => {
        const sortedProducts = payload.sort((a, b) => a.id - b.id);
        state.products = uniqBy(sortedProducts, "id");
        state.productsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchProducts.rejected, (state) => {
        state.productsLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchServiceStatus.fulfilled, (state, { payload }) => {
        state.serviceStatus = payload;
        state.serviceStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceMode.fulfilled, (state, { payload }) => {
        state.serviceMode = payload;
        state.serviceModeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPayStatus.fulfilled, (state, { payload }) => {
        state.payStatus = payload;
        state.payStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingObjectType.fulfilled, (state, { payload }) => {
        state.blockingObjectType = payload;
        state.blockingObjectTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingSource.fulfilled, (state, { payload }) => {
        state.blockingSource = payload;
        state.blockingSourceLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchRequestStatus.fulfilled, (state, { payload }) => {
        state.requestStatus = payload;
        state.requestStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchBlockingReason.fulfilled, (state, { payload }) => {
        state.blockingReason = payload;
        state.blockingReasonLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchUnBlockingReason.fulfilled, (state, { payload }) => {
        state.unBlockingReason = payload;
        state.unBlockingReasonLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchUserScope.fulfilled, (state, { payload }) => {
        state.userScope = payload;
        state.userScopeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceStatus.rejected, (state, action) => {
        state.serviceStatusLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchTaskStatusDict.fulfilled, (state, { payload }) => {
        state.taskStatus = [...payload].sort((a, b) => a.name.localeCompare(b.name));
        state.taskStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTaskStatusDict.rejected, (state, action) => {
        state.taskStatusLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchRateType.fulfilled, (state, { payload }) => {
        state.rateType = payload;
        state.rateTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchRateType.pending, (state) => {
        state.rateTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchObjectTypes.fulfilled, (state, { payload }) => {
        state.objectTypes = payload;
        state.objectTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchObjectTypes.pending, (state) => {
        state.objectTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicDataType.fulfilled, (state, { payload }) => {
        state.dicDataTypes = payload;
        state.dicDataTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicDataType.pending, (state) => {
        state.dicDataTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicViewModeMask.fulfilled, (state, { payload }) => {
        state.dicViewModeMasks = payload;
        state.dicViewModeMasksLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicViewModeMask.pending, (state) => {
        state.dicViewModeMasksLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicStorageType.fulfilled, (state, { payload }) => {
        state.dicStorageTypes = payload;
        state.dicStorageTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicStorageType.pending, (state) => {
        state.dicStorageTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicRequiredType.fulfilled, (state, { payload }) => {
        state.dicRequiredType = payload;
        state.dicRequiredTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicRequiredType.pending, (state) => {
        state.dicRequiredTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicMultiplType.fulfilled, (state, { payload }) => {
        state.dicMultiplType = payload;
        state.dicMultiplTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicMultiplType.pending, (state) => {
        state.dicMultiplTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicAdjustmentReason.pending, (state) => {
        state.dicAdjustmentReasonLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicAdjustmentReason.rejected, (state) => {
        state.dicAdjustmentReasonLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchDicAdjustmentReason.fulfilled, (state, { payload }) => {
        state.dicAdjustmentReason = sortBy(payload, "name");
        state.dicAdjustmentReasonLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicAdjustmentType.fulfilled, (state, { payload }) => {
        state.dicAdjustmentType = sortBy(payload, "id");
        state.dicAdjustmentTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicAdjustmentType.pending, (state) => {
        state.dicAdjustmentTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTrafficStatus.fulfilled, (state, { payload }) => {
        state.trafficStatus = payload;
        state.trafficStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTrafficStatus.rejected, (state, action) => {
        state.trafficStatusLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchDicActionType.fulfilled, (state, { payload }) => {
        state.actionType = payload;
        state.actionTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicActionType.pending, (state) => {
        state.actionTypeLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPaymentType.fulfilled, (state, { payload }) => {
        state.paymentType = payload;
        state.paymentTypeLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchCurrenciesDic.fulfilled, (state, { payload }) => {
        state.dicCurrencies = payload;
        state.dicCurrenciesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchCurrenciesDic.pending, (state) => {
        state.dicCurrenciesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchCurrenciesDic.rejected, (state, action) => {
        state.dicCurrenciesLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchInvoicePeriodStatus.fulfilled, (state, { payload }) => {
        state.invoicePeriodStatus = payload;
        state.invoicePeriodStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchInvoicePeriodStatus.pending, (state) => {
        state.invoicePeriodStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPaymentTypeStatus.fulfilled, (state, { payload }) => {
        state.paymentTypeStatus = payload;
        state.paymentTypeStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPaymentTypeStatus.pending, (state) => {
        state.paymentTypeStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPrePaymentPeriodStatus.fulfilled, (state, { payload }) => {
        state.prePaymentPeriodStatus = payload;
        state.prePaymentPeriodStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPrePaymentPeriodStatus.pending, (state) => {
        state.prePaymentPeriodStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchProfileTypeStatus.fulfilled, (state, { payload }) => {
        state.profileTypeStatus = payload;
        state.profileTypeStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchProfileTypeStatus.pending, (state) => {
        state.profileTypeStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPaymentStatuses.pending, (state) => {
        state.paymentStatusesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPaymentStatuses.fulfilled, (state, { payload }) => {
        state.paymentStatuses = payload;
        state.paymentStatusesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPaymentStatuses.rejected, (state) => {
        state.paymentStatuses = [];
        state.paymentStatusesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchTariffStatuses.fulfilled, (state, { payload }) => {
        state.tariffStatuses = payload;
        state.tariffStatusesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTariffStatuses.pending, (state) => {
        state.tariffStatusesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTariffTypes.pending, (state) => {
        state.tariffTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTariffTypes.fulfilled, (state, { payload }) => {
        state.tariffTypes = payload;
        state.tariffTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPeriodTypes.pending, (state) => {
        state.periodTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPeriodTypes.fulfilled, (state, { payload }) => {
        state.periodTypes = payload;
        state.periodTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchPriceUnits.pending, (state) => {
        state.priceUnitsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPriceUnits.fulfilled, (state, { payload }) => {
        state.priceUnits = payload;
        state.priceUnitsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchGlsTypes.pending, (state) => {
        state.glsTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchGlsTypes.fulfilled, (state, { payload }) => {
        state.glsTypes = payload;
        state.glsTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchGlsStatuses.pending, (state) => {
        state.glsStatusesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchGlsStatuses.fulfilled, (state, { payload }) => {
        state.glsStatuses = payload;
        state.glsStatusesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchChargeNames.pending, (state) => {
        state.chargeNamesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchChargeNames.fulfilled, (state, { payload }) => {
        const sortedData = payload.sort((a, b) => a.name.localeCompare(b.name, "ru"));
        state.chargeNamesLoading = rtkRequestStatusCodes.fulfilled;
        state.chargeNames = sortedData;
      })
      .addCase(fetchChargeNames.rejected, (state) => {
        state.chargeNamesLoading = rtkRequestStatusCodes.rejected;
        state.chargeNames = [];
      })
      .addCase(fetchChargeNameTypeStatus.fulfilled, (state, { payload }) => {
        state.chargeNameTypeStatus = payload;
        state.chargeNameTypeStatusLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchChargeNameTypeStatus.pending, (state) => {
        state.chargeNameTypeStatusLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicType.fulfilled, (state, { payload }) => {
        state.dicTypeWithParameter = [...payload].sort((a, b) => (a.name > b.name ? 1 : -1));
        state.dicTypeWithParameterLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicType.pending, (state) => {
        state.dicTypeWithParameterLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicType.rejected, (state) => {
        state.dicTypeWithParameterLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchDicTypeSpecific.fulfilled, (state, { payload }) => {
        state.dicTypeWithParameterSpecific = [...payload].sort((a, b) =>
          a.name > b.name ? 1 : -1
        );
        state.dicTypeWithParameterSpecificLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicTypeSpecific.pending, (state) => {
        state.dicTypeWithParameterSpecificLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicTypeSpecific.rejected, (state) => {
        state.dicTypeWithParameterSpecificLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchDicVersions.pending, (state) => {
        state.dicVersionsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicVersions.fulfilled, (state, { payload }) => {
        state.dicVersions = payload;
        state.dicVersionsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchDicVersions.rejected, (state, action) => {
        state.dicVersionsLoading = rtkRequestStatusCodes.rejected;
        Message(action.error.message);
      })
      .addCase(fetchSequence.pending, (state) => {
        state.sequenceLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchSequence.fulfilled, (state, { payload }) => {
        state.sequenceLoading = rtkRequestStatusCodes.fulfilled;
        state.sequence = payload;
      })
      .addCase(fetchSequence.rejected, (state) => {
        state.sequenceLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchDicRoutingOrdersStatuses.pending, (state) => {
        state.dicRoutingOrdersStatusesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchDicRoutingOrdersStatuses.fulfilled, (state, { payload }) => {
        state.dicRoutingOrdersStatusesLoading = rtkRequestStatusCodes.fulfilled;
        state.dicRoutingOrdersStatuses = payload;
      })
      .addCase(fetchPriceEvents.pending, (state) => {
        state.priceEventsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchPriceEvents.fulfilled, (state, { payload }) => {
        state.priceEvents = payload;
        state.priceEventsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchRateModes.pending, (state) => {
        state.rateModesEventsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchRateModes.fulfilled, (state, { payload }) => {
        state.rateModesEvents = payload;
        state.rateModesEventsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchConditionTypes.pending, (state) => {
        state.conditionTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchConditionTypes.fulfilled, (state, { payload }) => {
        state.conditionTypes = payload;
        state.conditionTypesLoading = rtkRequestStatusCodes.fulfilled;
      });
  },
});

const { actions, reducer } = slice;
export {
  actions,
  fetchObjectTypes,
  fetchDictionaries,
  fetchBillType,
  fetchBillStatus,
  fetchAgrStatus,
  fetchProducts,
  fetchServiceStatus,
  fetchServiceMode,
  fetchPayStatus,
  fetchBlockingObjectType,
  fetchUserScope,
  fetchTaskStatusDict,
  fetchDicDataType,
  fetchDicViewModeMask,
  fetchDicStorageType,
  fetchDicActionType,
  fetchTrafficStatus,
  fetchRetailNetworks,
  fetchServiceUsageDirection,
  fetchServiceUsageDocumentType,
  fetchCurrenciesDic,
  fetchPaymentType,
  fetchInvoicePeriodStatus,
  fetchPaymentTypeStatus,
  fetchPrePaymentPeriodStatus,
  fetchProfileTypeStatus,
  fetchChargeNameTypeStatus,
  fetchDicTypeSpecific,
  fetchDicType,
  fetchDicVersions,
  updateSequence,
  fetchSequence,
  fetchRateType,
  fetchDicRequiredType,
  fetchDicMultiplType,
  fetchDicAdjustmentType,
  fetchDicAdjustmentReason,
  fetchPaymentSourceTypes,
  fetchDicRoutingOrdersStatuses,
  fetchBlockingSource,
  fetchRequestStatus,
  fetchBlockingReason,
  fetchUnBlockingReason,
  fetchPriceEvents,
  fetchRateModes,
  fetchConditionTypes,
  fetchReportType,
};
export default reducer;
